/**
 * Local-SEO location data for ATC Home Pro's service area.
 *
 * Each entry produces:
 *   - A dedicated /locations/{slug} landing page
 *   - Combo /services/{service-slug}/{location-slug} pages (top services)
 *   - sitemap.xml entries
 *
 * The county/anchor copy is intentionally specific so Google sees unique
 * content per page (rather than thin duplicate town pages, which it penalizes).
 */

export const LOCATIONS = [
  {
    slug: "glen-rock-pa",
    name: "Glen Rock",
    state: "PA",
    full: "Glen Rock, PA",
    region: "Southern York County",
    anchor:
      "Home base. Our crew is dispatched from Glen Rock daily, which means faster response times and a deeper knowledge of local permitting, soil, and weather conditions than out-of-area contractors.",
    landmarks: ["Codorus Creek", "Glen Rock Borough", "Susquehanna Trail corridor"],
    nearby: ["Shrewsbury", "Loganville", "New Freedom", "Seven Valleys"],
  },
  {
    slug: "loganville-pa",
    name: "Loganville",
    state: "PA",
    full: "Loganville, PA",
    region: "Southern York County",
    anchor:
      "Home to our full design showroom on the Susquehanna Trail. Loganville homeowners can stop in, see materials in person, and walk through finished cabinetry, decking, and stone samples before signing off on a project.",
    landmarks: ["Loganville Borough", "Susquehanna Trail", "Centerville"],
    nearby: ["Glen Rock", "Seven Valleys", "Dallastown", "York"],
  },
  {
    slug: "shrewsbury-pa",
    name: "Shrewsbury",
    state: "PA",
    full: "Shrewsbury, PA",
    region: "Southern York County",
    anchor:
      "Shrewsbury is a heavy-traffic project area for ATC — lots of older brick and stone homes here mean tuned-up structural work, careful exterior matching, and respect for the borough's historic character.",
    landmarks: ["Shrewsbury Borough", "Exit 4 / I-83", "Stoney Brook"],
    nearby: ["New Freedom", "Glen Rock", "Stewartstown"],
  },
  {
    slug: "new-freedom-pa",
    name: "New Freedom",
    state: "PA",
    full: "New Freedom, PA",
    region: "Southern York County",
    anchor:
      "New Freedom's mix of mid-century homes and newer subdivisions keeps us busy with kitchens, decks, and full roof systems — most jobs are within 20 minutes of our shop.",
    landmarks: ["New Freedom Borough", "Heritage Rail Trail County Park"],
    nearby: ["Shrewsbury", "Stewartstown", "Glen Rock"],
  },
  {
    slug: "york-pa",
    name: "York",
    state: "PA",
    full: "York, PA",
    region: "York County",
    anchor:
      "Greater York is our largest city service area. Whether it's a row-home renovation downtown or an addition on a half-acre lot in the suburbs, our team has worked nearly every neighborhood from West York to Spring Garden.",
    landmarks: ["York City", "Spring Garden Township", "West York", "I-83 corridor"],
    nearby: ["Dallastown", "Spring Grove", "Hanover"],
  },
  {
    slug: "hanover-pa",
    name: "Hanover",
    state: "PA",
    full: "Hanover, PA",
    region: "Western York County",
    anchor:
      "Hanover homeowners get the same crew, same warranty, same response time as in-county clients. We routinely handle full kitchens, baths, and siding refreshes along the Route 116 corridor.",
    landmarks: ["Hanover Borough", "Route 116", "Codorus State Park"],
    nearby: ["Spring Grove", "York", "Northern Maryland"],
  },
  {
    slug: "spring-grove-pa",
    name: "Spring Grove",
    state: "PA",
    full: "Spring Grove, PA",
    region: "Western York County",
    anchor:
      "Spring Grove is a great mix of country properties and tight downtown lots — perfect for our specialty in custom decks, additions, and detached structures. Expect long-lasting work with proper drainage and structural prep.",
    landmarks: ["Spring Grove Borough", "Codorus Creek headwaters"],
    nearby: ["Hanover", "York", "Glen Rock"],
  },
  {
    slug: "stewartstown-pa",
    name: "Stewartstown",
    state: "PA",
    full: "Stewartstown, PA",
    region: "Eastern York County",
    anchor:
      "Stewartstown's older farm properties and newer suburban builds both benefit from our same-team approach — no subbing out roofing, then again for siding, then again for concrete. One crew, one warranty, one schedule.",
    landmarks: ["Stewartstown Borough", "Route 24"],
    nearby: ["Shrewsbury", "New Freedom"],
  },
  {
    slug: "seven-valleys-pa",
    name: "Seven Valleys",
    state: "PA",
    full: "Seven Valleys, PA",
    region: "Southern York County",
    anchor:
      "Quick reach for our Glen Rock crew. Common projects here are deck replacements, kitchen refreshes, and concrete patios that hold up to PA freeze-thaw.",
    landmarks: ["Seven Valleys Borough", "Codorus Creek"],
    nearby: ["Loganville", "Glen Rock", "Dallastown"],
  },
  {
    slug: "dallastown-pa",
    name: "Dallastown",
    state: "PA",
    full: "Dallastown, PA",
    region: "Southern York County",
    anchor:
      "Dallastown's mature neighborhoods see a lot of remodel & addition work from us. We're careful about driveway protection, dumpster placement, and finishing on time so the street isn't disrupted longer than it needs to be.",
    landmarks: ["Dallastown Borough", "Route 74"],
    nearby: ["York", "Red Lion", "Loganville"],
  },
  {
    slug: "southern-york-county-pa",
    name: "Southern York County",
    state: "PA",
    full: "Southern York County, PA",
    region: "Southern York County",
    anchor:
      "Our core service zone. Within a 1-hour radius we cover every township south of York City — Shrewsbury, Glen Rock, Loganville, Stewartstown, New Freedom, Seven Valleys, Dallastown — and the surrounding rural addresses.",
    landmarks: ["Southern York Township", "Codorus Township", "Shrewsbury Township"],
    nearby: ["York", "Hanover", "Northern Maryland"],
  },
  {
    slug: "northern-maryland",
    name: "Northern Maryland",
    state: "MD",
    full: "Northern Maryland",
    region: "Northern Maryland",
    anchor:
      "We cross the PA line for projects in Northern Maryland — Parkton, Hereford, Monkton, White Hall, Freeland. Same lifetime labor warranty, same crew, no out-of-state surcharge.",
    landmarks: ["Hereford", "Parkton", "White Hall", "I-83 corridor"],
    nearby: ["Shrewsbury", "New Freedom", "Stewartstown"],
  },
];

export function findLocation(slug) {
  return LOCATIONS.find((l) => l.slug === slug) || null;
}

/**
 * Services that get their own service+location landing page (the top
 * converting & highest-volume combos). Keep this short — Google penalizes
 * thin duplicate location pages, and 4 well-written pages per town >> 7
 * weak ones.
 */
export const COMBO_SERVICE_SLUGS = [
  "custom-decks",
  "custom-kitchens",
  "concrete-work",
  "roofing",
];
